import React, { useCallback, useState } from 'react';
import { Layers, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import { openComposeInNewTab } from '../utils/composeNavigation';

const DISMISS_STORAGE_KEY = 'ums-compose-multi-window-hint-dismissed-v2';

export function getNewMessageMultiWindowHintTitle(): string {
  return 'New message (opens in a new tab)';
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, '1');
  } catch {
    // ignore
  }
}

export interface ComposeMultiWindowHintProps {
  colors: ThemeColors;
}

/** Tip shown on blank compose — each New message lives in its own browser tab. */
const ComposeMultiWindowHint: React.FC<ComposeMultiWindowHintProps> = ({ colors }) => {
  const [dismissed, setDismissed] = useState(readDismissed);

  const onDismiss = useCallback(() => {
    writeDismissed();
    setDismissed(true);
  }, []);

  if (dismissed) return null;

  return (
    <div
      className={cn(
        'flex-shrink-0 border-b px-4 sm:px-5 py-2.5',
        'bg-gradient-to-r from-indigo-50/90 via-sky-50/60 to-transparent',
        'dark:from-indigo-950/40 dark:via-sky-950/20 dark:to-transparent',
        colors.border,
      )}
      role="note"
      aria-label="How to open multiple compose windows"
    >
      <div className="flex items-start gap-3 min-w-0">
        <div
          className={cn(
            'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
            'bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/10 dark:text-indigo-300',
          )}
          aria-hidden
        >
          <Layers className="h-4 w-4" strokeWidth={1.75} />
        </div>

        <div className="min-w-0 flex-1 space-y-1">
          <p className={cn('text-sm font-medium', colors.text)}>Work on multiple drafts at once</p>
          <p className={cn('text-xs leading-relaxed', colors.textMuted)}>
            Every <span className="font-medium">+ New message</span> opens in its own browser tab, so you can keep
            several drafts open side by side. Sending, saving or discarding closes the tab and returns you to the
            inbox.{' '}
            <button
              type="button"
              onClick={() => openComposeInNewTab()}
              className="font-medium text-indigo-700 underline decoration-indigo-300/70 underline-offset-2 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200"
            >
              Open another new message
            </button>
          </p>
        </div>

        <button
          type="button"
          onClick={onDismiss}
          className={cn(
            'flex-shrink-0 rounded-md p-1.5 transition-colors',
            colors.textMuted,
            colors.interactiveHover,
          )}
          aria-label="Dismiss tip"
          title="Dismiss"
        >
          <X className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );
};

export default ComposeMultiWindowHint;
